import { useEffect, useState, useRef, useCallback } from "react";
import { Send, Trash2, MessageCircle, Copy, ClipboardList, Check } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";

const AGENTS = [
  { id: "lea", label: "Léa", desc: "Architecte principale" },
  { id: "lea_nexus", label: "Léa NEXUS", desc: "Conseillère écosystème" },
  { id: "nexus", label: "NEXUS", desc: "Cerveau central" },
];

const STYLES = {
  mario: { side: "justify-end", box: "border-border bg-card", name: "text-white" },
  lea: { side: "justify-start", box: "border-rose-500/30 bg-rose-500/5", name: "text-rose-400" },
  lea_nexus: { side: "justify-start", box: "border-cyan-500/30 bg-cyan-500/5", name: "text-cyan-400" },
  nexus: { side: "justify-start", box: "border-emerald-500/30 bg-emerald-500/5", name: "text-emerald-400" },
};

const PROVENANCE = {
  lea: "Persona IA hébergée par NEXUS · réflexion, jamais un fait",
  lea_nexus: "Persona IA hébergée par NEXUS · conseil, jamais un fait",
  nexus: "Cerveau NEXUS · données sourcées",
};

async function copyText(text, okMsg) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(okMsg);
    } catch {
      toast.error("Copie impossible dans ce navigateur");
    }
  }
}

function Bubble({ m }) {
  const s = STYLES[m.author] ||
    (m.is_external
      ? { side: "justify-start", box: "border-violet-500/30 bg-violet-500/5", name: "text-violet-400" }
      : STYLES.mario);
  const prov = m.is_external ? "Conseillère externe (canal API) · déclaration, jamais un fait" : PROVENANCE[m.author] || "";
  const copyMsg = () => copyText(`${m.author_name} [${fmtDate(m.created_at)}] :\n${m.content}`, "Message copié");
  return (
    <div className={`group flex ${s.side}`}>
      <div className={`relative max-w-[80%] border px-4 py-3 ${s.box}`} title={prov}>
        <button data-testid={`salon-copy-${m.id}`} onClick={copyMsg} title="Copier ce message"
          className="absolute right-2 top-2 text-neutral-600 opacity-0 transition-opacity hover:text-white group-hover:opacity-100">
          <Copy size={13} />
        </button>
        <div className="flex flex-wrap items-baseline gap-x-3 font-mono text-[10px] uppercase tracking-[0.15em]">
          <span className={s.name}>{m.author_name}</span>
          <span className="text-neutral-600">{fmtDate(m.created_at)}</span>
          {m.sources?.length > 0 && <span className="text-neutral-500">{m.sources.length} sources</span>}
          {m.order_id && <span className="text-amber-300">→ ordre {m.order_id.slice(0, 8)} (signature requise)</span>}
          {m.addressed_to?.length > 0 && (
            <span className="text-neutral-600">→ {m.addressed_to.map((t) => AGENTS.find((a) => a.id === t)?.label || t).join(", ")}</span>
          )}
        </div>
        <div className="mt-1.5 whitespace-pre-wrap text-[15px] leading-relaxed text-neutral-200">{m.content}</div>
      </div>
    </div>
  );
}

export default function Salon() {
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState([]);
  const [input, setInput] = useState("");
  const [targets, setTargets] = useState(["nexus"]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const countRef = useRef(0);

  const load = useCallback(() => {
    api.get("/salon/messages").then(({ data }) => {
      setMessages(data.messages);
      setTyping(data.typing);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (messages.length !== countRef.current) {
      countRef.current = messages.length;
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const toggle = (id) =>
    setTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const send = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await api.post("/salon/messages", { content, addressed_to: targets });
      setInput("");
      load();
    } catch {
      toast.error("Envoi impossible — réessayez");
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Effacer tout l'historique du salon ? Cette action est définitive.")) return;
    await api.post("/salon/clear");
    load();
  };

  return (
    <div data-testid="salon-page" className="relative flex h-[calc(100vh-7rem)] flex-col">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-end justify-between pb-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Salon</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            Espace de travail commun — Mario · Léa · Léa NEXUS · NEXUS. Identités et autorités séparées.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="salon-copy-all-btn"
            onClick={() => {
              const text = messages.map((m) => `${m.author_name} [${fmtDate(m.created_at)}] :\n${m.content}`).join("\n\n---\n\n");
              copyText(text, `Fil copié (${messages.length} messages)`);
            }}
            className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500 hover:text-cyan-300">
            <ClipboardList size={13} /> Copier le fil
          </button>
          <button data-testid="salon-clear-btn" onClick={clear}
            className="flex items-center gap-1.5 border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500 hover:text-red-400">
            <Trash2 size={13} /> Effacer
          </button>
        </div>
      </div>

      <div data-testid="salon-thread" className="relative flex-1 space-y-4 overflow-y-auto border border-white/10 bg-background p-5">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <MessageCircle size={28} className="text-neutral-700" />
            <div className="font-mono text-xs text-neutral-600">
              Salon vide. Choisissez vos destinataires ci-dessous et lancez la conversation.<br />
              Astuce : mentionnez @Léa, @Léa NEXUS ou @NEXUS directement dans votre message.
            </div>
          </div>
        )}
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        {typing.length > 0 && (
          <div data-testid="salon-typing" className="flex items-center gap-2 font-mono text-xs text-neutral-500">
            <span className="inline-flex gap-1">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-500" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-500 [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-500 [animation-delay:300ms]" />
            </span>
            {typing.map((t) => AGENTS.find((a) => a.id === t)?.label || t).join(", ")} écrit…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border border-t-0 border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2 pb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">S'adresser à :</span>
          {AGENTS.map((a) => (
            <button key={a.id} data-testid={`salon-target-${a.id}`} onClick={() => toggle(a.id)}
              title={targets.includes(a.id) ? `${a.desc} — activé, répondra à votre message` : `${a.desc} — cliquer pour activer`}
              className={`flex items-center gap-1.5 border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                targets.includes(a.id)
                  ? a.id === "nexus" ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                    : a.id === "lea" ? "border-rose-500/60 bg-rose-500/10 text-rose-300"
                    : "border-cyan-500/60 bg-cyan-500/10 text-cyan-300"
                  : "border-border text-neutral-600 hover:text-neutral-400"}`}>
              {targets.includes(a.id) && <Check size={11} strokeWidth={3} />}
              {a.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <textarea data-testid="salon-input" value={input} rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Votre message… (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
            className="flex-1 resize-none border border-border bg-background px-4 py-3 text-[15px] text-white placeholder:text-neutral-600 focus:border-cyan-500/50 focus:outline-none" />
          <button data-testid="salon-send-btn" onClick={send} disabled={sending || !input.trim()}
            className="flex items-center gap-2 border border-cyan-500/50 bg-cyan-500/10 px-5 font-mono text-xs uppercase tracking-[0.15em] text-cyan-300 shadow-[0_0_14px_rgba(6,182,212,0.12)] hover:bg-cyan-500/20 disabled:opacity-40">
            <Send size={14} /> {sending ? "…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}

